import { useEffect, type ReactNode } from 'react';
import { useIsMobile } from '../lib/useMediaQuery';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Si es true, el hijo siempre se renderiza inline (sin sheet). */
  inlineOnDesktop?: boolean;
  /** Si es true, en escritorio se muestra como modal centrado en vez de nada. */
  desktopModal?: boolean;
}

export function MobileFormSheet({
  open,
  onClose,
  title,
  children,
  inlineOnDesktop = false,
  desktopModal = false,
}: Props) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!isMobile && inlineOnDesktop) {
    return <>{children}</>;
  }

  if (!isMobile && desktopModal) {
    if (!open) return null;
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <h2 className="text-base font-semibold">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 p-2 -mr-2"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          <div className="overflow-y-auto px-4 py-4">{children}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
          aria-hidden
        />
      )}
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl
          transform transition-transform duration-250 ease-out
          ${open ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ maxHeight: '90vh', paddingBottom: 'var(--safe-bottom)' }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="py-2">
          <div className="sheet-handle" />
        </div>
        <div className="px-4 pb-3 pt-1 flex items-center justify-between border-b border-slate-100">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-2 -mr-2 tap"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <div
          className="overflow-y-auto px-4 py-4"
          style={{ maxHeight: 'calc(90vh - 60px - var(--safe-bottom))' }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
